import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { unitId as brandUnitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { Note } from '@/components/Note'
import { GridTable } from '@/components/GridTable'
import { WizardProse, WizardButtons, WizardHeading } from '@/components/WizardParts'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { UNEXPECTED_ERROR_COPY } from '@/copy/registry'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { buildRestoreInput } from '@/layers/restore-wizard/buildRestoreInput'
import { formatJobTime, TRIGGER_LABEL } from '@/screens/shared/jobFormatting'
import { MStepup } from '@/screens/m-stepup/MStepup'
import { MRunok } from '@/screens/m-runok/MRunok'
import type { Problem } from '@/data/apiClient'
import type { RestoreWizardConfig, RestorePlan, RestoreConfirmation, JobRef, WizardStage } from '@/layers/restore-wizard/types'

/** `trigger.py`/`_assertion.py` raise exactly these three for the minted step-up assertion — never
 * from `create_confirmation.py` (which mints it), always from `/restores` itself (which spends it,
 * `trigger` below) — so this pane, not `MStepup`, is the one that ever sees them. Copy matches each
 * kind's own stated remedy (`RefusalKind.yaml` rows 25-27, "ask for a fresh confirmation" / "return
 * the operator to the confirmation step"); `ASSERTION_PAYLOAD_MISMATCH` cannot name the specific
 * changed input client-side (the server returns a hash mismatch, not a diff), so this points back at
 * the write order above instead of inventing one. */
const ASSERTION_REFUSAL_COPY: Record<string, string> = {
  ASSERTION_SPENT: 'This confirmation has already been used. Get a fresh one before running this restore.',
  ASSERTION_EXPIRED: "This confirmation has expired — it's only valid for five minutes. Get a fresh one to continue.",
  ASSERTION_PAYLOAD_MISMATCH: 'Something about what you confirmed has changed since you confirmed it. Review the write order above, then get a fresh confirmation.',
}

/**
 * The `order` role's shared pane (`repo-wz-4`/…/`ref-wz-5`/…): "**Write order, and the commit.**"
 * Re-computes the plan on entry (the "fired without a control" table's own item: the control that
 * asked for it was on the `plan` pane) so the write-order table here reflects exactly what
 * `restoreCreateConfirmation` will hash. `Run this restore` opens `m-stepup` (W144) — step-up is
 * required on every restore (§18.1) and never fires from a control of its own — and on a minted
 * assertion fires `restoreTrigger`, landing on `m-runok` (W144) for the success outcome.
 *
 * **`panel-header` added W159** — a "Write order" title above the table, matching `PlanPane`'s own
 * identically-named panel exactly.
 *
 * **`chip` added W166** — the meta-block's own "Kind" value, previously plain text, matching the
 * same categorical-value-as-`Chip` convention `v-backupjob`/`v-restorejob`'s own meta-blocks use.
 *
 * **`meta-block` given its own real border/padding/`--gradient-card` background (W173)** — this
 * pane's own `.meta` sits directly on the `pane`, never nested inside another bordered panel, and
 * the prototype's own `.meta` rule draws a real bordered, gradient-backed card in that position
 * (`border:1px solid var(--bd);border-radius:4px;background:linear-gradient(...)`) — confirmed
 * against `repo-wz-4`'s own markup. Where a screen's `meta-block` sits INSIDE another panel instead
 * (`v-credential`'s "Status" panel, `v-restorejob`'s "Job details" panel), the prototype's own rule
 * explicitly overrides itself to `border:0;background:none;padding:0` — those stay bare on purpose,
 * not an inconsistency.
 *
 * **`meta-block` filled out to 4 of the prototype's 6 kv pairs, and `acknowledgement` added.** The
 * prototype's per-machine `.meta` blocks (`repo-wz-4`, `ref-wz-5`, `project-wz-4`, …) each show 6
 * pairs, but two of them — `Run as` ("Manual only · never scheduled") and `Estimated` ("Within 30
 * minutes at p95") — are static prototype copy with no backing field anywhere in `RestorePlan` or
 * `RestoreInput`; rendering them here would be inventing data the server never served. Added
 * `Restore point` and `Scope`, both real (`stage.restorePoint`, the same repository/project-count
 * summary `MStepup`'s own `scopeSummary` already computed) — `Run as`/`Estimated` are a reported gap,
 * not built around silently. The acknowledgement checkbox (spec's own `acknowledgement` component)
 * now gates `Run this restore`, matching the prototype's `data-need="1"` — previously the button
 * enabled on `plan.data` alone with no read-and-accept step.
 *
 * **Per-kind handling for the three assertion refusals added (OI-C206, `TC-RST-STEP-005`).** All
 * three (`ASSERTION_SPENT`/`ASSERTION_EXPIRED`/`ASSERTION_PAYLOAD_MISMATCH`) previously fell through
 * to `trigger.error`'s bare `problem.title` here, with no labelled remedy and no correlation id —
 * dispatched as an `MStepup` finding, but verified directly against `trigger.py`/`_assertion.py` that
 * none of the three can ever come from `create_confirmation.py` (which mints the assertion, no
 * comparison possible yet); `TC-RST-STEP-005` itself (unlike the dispatch summary) already had this
 * right — all three are `/restores`' own refusals (which spends it), surfacing through this pane's
 * `problem`, never `MStepup`'s. "Get a fresh confirmation" resets the stale mutation, refetches the
 * plan so the next assertion covers the current `plan_digest`, and reopens the step-up modal — the
 * same manual path `Run this restore` already offered, just named and explained instead of left to a
 * bare title. `correlation_id` is required on every `Problem` (`Problem.yaml`), not just the two
 * `isUnexpectedRefusal` kinds, so it's shown here too via the same `UNEXPECTED_ERROR_COPY` template.
 */
export function OrderPane({
  config,
  heading,
  stage,
  onBack,
}: {
  config: RestoreWizardConfig
  heading: string
  stage: Extract<WizardStage, { stage: 'order' }>
  onBack: () => void
}) {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const navigate = useNavigate()
  const [stepupOpen, setStepupOpen] = useState(false)
  const [confirmation, setConfirmation] = useState<RestoreConfirmation>()
  const [job, setJob] = useState<JobRef>()
  const [acknowledged, setAcknowledged] = useState(false)

  const plan = useQuery({
    queryKey: ['restore-plan-order', orgId, workspaceId, stage],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<RestorePlan> => {
      const client = getApiClient()
      const { data, error } = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/restore-plans', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: buildRestoreInput(config, stage),
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as RestorePlan
    },
  })

  const trigger = useMutation({
    mutationFn: async (assertion: string): Promise<JobRef> => {
      const client = getApiClient()
      const restoreInput = buildRestoreInput(config, stage, plan.data?.plan_digest)
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/restores', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: { ...restoreInput, assertion } as never,
      })
      return unwrap(response as { data?: JobRef; error?: Problem })
    },
    onSuccess: setJob,
  })

  useEffect(() => {
    if (confirmation) trigger.mutate(confirmation.assertion)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmation])

  const problem = plan.error instanceof ApiProblemError ? plan.error.problem : trigger.error instanceof ApiProblemError ? trigger.error.problem : undefined
  const scopeSummary = stage.repositoryIds?.length
    ? `${stage.repositoryIds.length} ${stage.repositoryIds.length === 1 ? 'repository' : 'repositories'}`
    : stage.projectKeys?.length
      ? `${stage.projectKeys.length} ${stage.projectKeys.length === 1 ? 'project' : 'projects'}`
      : 'the whole workspace'

  return (
    <div data-scr={brandUnitId(config.unitIds.order)} data-cmp="pane">
      {/* `repo-wz-4`'s own shape: no `wizsplit` at all, unlike every other role here — full width
       * from the heading down, matching the prototype exactly (confirmed against its markup). */}
      <WizardHeading>{heading}</WizardHeading>
      <WizardProse>The write order is derived from each object's own restorability — protection writes sort last.</WizardProse>
      {problem &&
        (isUnexpectedRefusal(problem.refusal_kind) ? (
          <UnexpectedRefusal problem={problem} />
        ) : ASSERTION_REFUSAL_COPY[problem.refusal_kind] ? (
          <Note>
            {ASSERTION_REFUSAL_COPY[problem.refusal_kind]} {UNEXPECTED_ERROR_COPY.detailTemplate(problem.correlation_id)}{' '}
            <button
              type="button"
              data-cmp="link"
              className="underline text-link bg-transparent border-0 p-0 cursor-pointer"
              onClick={async () => {
                trigger.reset()
                setConfirmation(undefined)
                await plan.refetch()
                setStepupOpen(true)
              }}
            >
              Get a fresh confirmation
            </button>
          </Note>
        ) : (
          <Note>{problem.title}</Note>
        ))}

      {plan.data && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-6">
          <div data-cmp="panel-header" className="p-5 border-b border-bd font-semibold text-body-lg">
            Write order
          </div>
          <GridTable
            columns={[
              { key: 'order', header: 'Order', render: (phase) => phase.order },
              { key: 'action', header: 'Action', render: (phase) => phase.action_expected },
            ]}
            rows={plan.data.phases}
            getRowKey={(phase) => String(phase.order)}
          />
        </div>
      )}

      <div data-cmp="meta-block" className="grid gap-3 mb-6 p-5 rounded-2 border border-bd" style={{ gridTemplateColumns: '160px 1fr', background: 'var(--gradient-card)' }}>
        <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
          Kind
        </div>
        <div data-cmp="kv-value" className="text-body-lg">
          <Chip variant="plain">{config.kindLabel}</Chip>
        </div>
        <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
          Restore point
        </div>
        <div data-cmp="kv-value" className="text-body-lg">
          <span className="font-mono">
            {stage.restorePoint.data_snapshot.handle} ·{' '}
            {stage.restorePoint.started_at ? stage.restorePoint.started_at.display : formatJobTime(stage.restorePoint.data_snapshot.started_at)}
          </span>
          {stage.restorePoint.configuration_snapshot && (
            <Note>
              {TRIGGER_LABEL[stage.restorePoint.data_snapshot.trigger] ?? stage.restorePoint.data_snapshot.trigger} · configuration{' '}
              {stage.restorePoint.configuration_snapshot.handle} · {formatJobTime(stage.restorePoint.configuration_snapshot.started_at)}
            </Note>
          )}
        </div>
        <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
          Scope
        </div>
        <div data-cmp="kv-value" className="text-body-lg">{scopeSummary}</div>
        <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
          Destination
        </div>
        <div data-cmp="kv-value" className="text-body-lg">{stage.destinationLabel}</div>
      </div>

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-6 p-5">
        <label data-cmp="acknowledgement" className="flex items-start gap-3 text-body-lg">
          <input type="checkbox" className="mt-1" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
          <span>
            I have read what this restore will attempt, and I accept the write order.
            <Note>
              This is what the restore will attempt; the destination is read only as each object is written. Your acknowledgement is
              recorded on the job and is covered by the confirmation code, so changing any input asks for a new one.
            </Note>
          </span>
        </label>
      </div>

      <WizardButtons>
        <Button data-act={actionId(`nav.${config.unitIds.plan}`)} ghost onClick={onBack}>
          Back
        </Button>
        <Button data-act={actionId('restore.trigger')} disabled={!plan.data || !acknowledged} onClick={() => setStepupOpen(true)}>
          Run this restore
        </Button>
      </WizardButtons>

      {stepupOpen && plan.data && (
        <MStepup
          orgId={orgId}
          workspaceId={workspaceId}
          restoreInput={buildRestoreInput(config, stage, plan.data.plan_digest)}
          scopeSummary={scopeSummary}
          kindLabel={config.kindLabel}
          destinationLabel={stage.destinationLabel}
          onDismiss={() => setStepupOpen(false)}
          onConfirmed={(c) => {
            setStepupOpen(false)
            setConfirmation(c)
          }}
        />
      )}

      {job && (
        <MRunok
          job={job}
          notifyEmail="platform-oncall@northwind.example"
          onDismiss={() => undefined}
          onOpenJob={() => navigate(`/jobs/restore/${job.job_id}`)}
        />
      )}
    </div>
  )
}
